import React, { type JSX } from 'react';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import Table, { type TableColumn } from '../Table/Table';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '@resources/js/contexts/useIconDefaults';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import useTheme from '@resources/js/contexts/useTheme';
import VerticalContent from '../VerticalContent/VerticalContent';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export type BuildsSeasonalGemsSectionViewModel = {
  title: string;
  description: string;
  table: {
    columns: Record<
      'item' | 'popularity',
      BuildsSeasonalGemsSectionViewModelColumn
    > &
      Partial<Record<'maxKey', BuildsSeasonalGemsSectionViewModelColumn>>;
    data: Array<{
      item: string;
      maxKey?: string;
      dps: string;
      hps?: string;
      popularity: string;
    }>;
  };
};

export type BuildsSeasonalGemsSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  item: { size: iconSizeLarge },
};

export default function BuildsSeasonalGemsSection({
  title,
  description,
  table,
}: BuildsSeasonalGemsSectionViewModel): JSX.Element {
  const theme = useTheme();

  return (
    <VerticalContent>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {table.data.length > 0 ? (
          <Table
            {...{
              ...table,
              columns: {
                ...table.columns,
                item: {
                  ...table.columns.item,
                  maxWidth: 500,
                },
              },
            }}
            enhancedMarkdownEnabled={true}
            truncate={{
              limit: 6,
              showMoreButton: { styles: theme.primaryButtonStyles },
            }}
          />
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
